import React from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Modal from '@ff/ui/Modal';
import Text from '@ff/ui/Text';
import Image from '@ff/ui/Image';
import type { Props as PropsSchema } from './props-schema';
import FlexCol from '@ff/ui/FlexCol';
import CloseSVG from './Icons/close.svg';

export interface Props {
  content: PropsSchema['bioList'][0];
  onClose: () => void;
}

export default function BioModal({ onClose, content }: Props) {
  return (
    <Modal 
      open={!!content} 
      onClose={onClose} 
      backdrop 
      closeOnBackdrop 
      styleProps={{
        // To hide border radius outline around the image
        borderRadius: content?.profileRoundedCorner ? '12px' : '3px',
      }}
    >
      <Container>
        {/* Custom Close Icon */}
        <StickyClose tabEnter onClick={onClose}>
          <StyledCloseIcon src={CloseSVG} />
        </StickyClose>

        {/* Content */}
        <Layout>
          <ImageContainer>
            <ProfileImage
              src={content?.profileUrl}
              alt={content?.profileUrlAlt || 'profile'}
              profileRoundedCorner={content?.profileRoundedCorner}
            />
          </ImageContainer>
          <TextContent>
            <Scrollable>
              <Name>{content?.name}</Name>
              <JobPostion>{content?.position}</JobPostion>
              <Bio>{content?.bio}</Bio>
            </Scrollable>
          </TextContent>
        </Layout>
      </Container>
    </Modal>
  );
}

const Name = styled(Text)({
  color: 'rgba(23, 26, 32, 1)',
  fontSize: '24px',
  fontWeight: 500,
  lineHeight: '22px',
  marginBottom: '7px',
});

const JobPostion = styled(Text)({
  color: 'rgba(106, 108, 112, 1)',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '22px',
});

const Bio = styled(Text)(({ theme }) => ({
  color: 'rgba(63, 63, 67, 1)',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '22px',
  paddingTop: '28px',
  [theme.mediaQueries.md]: {
    paddingTop: '32px',
  },
}));

const Container = styled(Box)(({ theme }) => ({
  maxHeight: 'min(700px, 85vh)',
  minHeight: '50vh',
  overflowY: 'auto',
  position: 'relative',
  width: '80vw',
  '::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
  '::-webkit-scrollbar-thumb': {
    background: 'rgba(23, 26, 32, 0.19)',
  },
  [theme.mediaQueries.sm]: {
    width: '450px',
  },
  [theme.mediaQueries.md]: {
    height: '554px',
    minHeight: '100%',
    overflowY: 'hidden',
    width: '888px',
    '::-webkit-scrollbar': {
      display: 'block',
      width: '6px',
    },
  },
}));

const Layout = styled(FlexCol)(({ theme }) => ({
  height: '100%',
  justifyContent: 'space-between',
  width: '100%',
  [theme.mediaQueries.md]: {
    flexDirection: 'row',
  },
}));

const StickyClose = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(226, 226, 226, 0.8)',
  borderRadius: '50%',
  cursor: 'pointer',
  float: 'right',
  marginRight: '10px',
  marginTop: '-30px',
  padding: '9px',
  position: 'sticky',
  top: '10px', // needed for sticky to work
  [theme.mediaQueries.md]: {
    top: '12px',
    backgroundColor: 'rgba(116, 116, 128, 0.08)',
    marginRight: '14px',
  },
}));

const StyledCloseIcon = styled(Image)({
  width: '12px',
});

const ImageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.mediaQueries.md]: {
    maxWidth: '422px',
  },
}));

const ProfileImage = styled(Image)<{ profileRoundedCorner?: boolean }>(({ theme, profileRoundedCorner }) => ({
  borderTopLeftRadius: profileRoundedCorner ? '12px' : '3px',
  borderTopRightRadius: profileRoundedCorner ? '12px' : '3px',
  height: '100%',
  maxHeight: '390px',
  objectFit: 'cover',
  objectPosition: 'top',
  width: '100%',
  [theme.mediaQueries.md]: {
    borderTopRightRadius: 0,
    borderBottomLeftRadius: profileRoundedCorner ? '12px' : '3px',
    maxHeight: '100%',
    minHeight: '554px',
    objectPosition: 'center',
  },
}));

const TextContent = styled(Text)(({ theme }) => ({
  width: '100%',
  [theme.mediaQueries.md]: {
    maxWidth: '444px',
  },
}));

const Scrollable = styled(Box)(({ theme }) => ({
  marginRight: '8px',
  overflowY: 'hidden',
  paddingBottom: '44px',
  paddingLeft: '24px',
  paddingRight: '24px',
  paddingTop: '24px',
  '::-webkit-scrollbar': {
    display: 'none',
    width: 0,
  },
  '::-webkit-scrollbar-thumb': {
    background: 'rgba(23, 26, 32, 0.19)',
  },
  '::-webkit-scrollbar-track': {
    marginBottom: '30px',
    marginTop: '52px',
  },
  [theme.mediaQueries.md]: {
    maxHeight: '554px',
    overflowY: 'auto',
    paddingBottom: '24px',
    paddingLeft: '5px',
    paddingRight: '24px',
    paddingTop: '64px',
    '::-webkit-scrollbar': {
      display: 'block',
      width: '3px',
    },
  },
}));
